import { useState, useEffect } from "react";
import { useHistory } from 'react-router';
import moment from "moment";
import _, { debounce } from 'lodash';

import { Alert, AutoComplete, Button, Card, Collapse, Divider, Form, Input, Modal, PageHeader, Radio, Select, Skeleton, Spin, Table, Tag, Tooltip } from "antd";
import Title from 'antd/lib/typography/Title';

import { ErrorResponse } from "utils/types";
import notifier from 'utils/notifier';
import { formatDate, calcAgeInMonths, convertHeight, calculateHeightInInches, computeBmiPercentile, computeBMI } from "utils/helpers";
import { BLOOD_PRESSURE_MEASURE_CODES } from 'utils/objects';
import { ModalTitles } from 'utils/enums';

import { CategoryModel } from "store/eligibility/documents/actions";

import FloatLabel from "components/Common/FloatLabel";
import UploadDocumentModal from "components/Eligibility/MemberEligibilityDetails/UploadDocumentModal";
import { CptCodeComponent, DatepickerComponent, IcdCodeComponent, ScreeningComponent, MeasureStatusComponent, CompletionProgressComponent } from "components/Eligibility/ClaimGenerator/MeasureList";
import constants from 'constants/index';

import { MEDICAL_RX_URL, COVER_MY_MED_URL } from './urls';
import './index.scss';

const { Option } = Select;
const { Panel } = Collapse;

interface ClaimGeneratorProps {
  getMemberMeasures(memberInfo: any, measureYear: number, memberMeasureStatus: string): any;
  getHedisMeasures(measureCode: string, measureName: string): any;
  getMemberImmunization(mpi: string): any;
  getEncounter(companyId: any, membMpiNo: any, dos: string): any;
  saveEncounter(details: any, memberInfo: any, dos: string): any;
  saveMemberMeasure(measureCode: string, hedisMeasureDetailId: number,
                      hedisMeasureDetailId2: number, measureFormDetails: any,
                      memberInfo: any, providerInfo: any,
                      claimStatus: string, measureStatus: string,
                      measureYear: number, documentId?: number,
                      claimReferenceNo?: string): any;
  getCategory(model: CategoryModel): any;
  uploadFilestoAuth(options: Object): any;
  getEmCode(payload: Object): any;
  getAuthModifierCode(): any;
  validateMeasures(payload: any): any;
  submitMemberMeasures(payload: any): any;
  saveHedisClaim(payload: any): any;
  eligibilityMemberInfo: any;
  primaryCareProvider: any;
  isUploading: boolean;
  isFetchingMember: boolean;
  isGetting: boolean;
  isFetchingEmCodes: boolean;
  measureYear: number;
}


const ClaimGenerator = (props: ClaimGeneratorProps) => {
  const history = useHistory();

  const [form] = Form.useForm();
  const [dos, setDos] = useState<string>(moment('01/01/' + props.measureYear).startOf('year').format('MM/DD/YYYY'));
  const [inCompletedMeasures, setInCompletedMeasures] = useState<any>([]);
  const [inProgressMeasures, setInProgressMeasures] = useState<any>([]);
  const [completedMeasures, setCompletedMeasures] = useState<any>([]);
  const [readyToSubmitMeasures, setReadyToSubmitMeasures] = useState<any>([]);

  const [hasControllingBloodPressureMeasure, setHasControllingBloodPressureMeasure] = useState(false);
  const [hasCareForAdultsMedReviewMeasure, setHasCareForAdultsMedReviewMeasure] = useState(false);

  const [memberImmunization, setMemberImmunization] = useState<any[]>([]);
  const [memberVitalDetail, setMemberVitalDetail] = useState<any>({});
  const [ageInMonths, setAgeInMonths] = useState(calcAgeInMonths(props.eligibilityMemberInfo.RmiBirth));
  const [isUploadDocumentModalVisible, setIsUploadDocumentModalVisible] = useState(false);
  const [showIcdCode, setShowIcdCode] = useState(false);
  const [showIcdCode2, setShowIcdCode2] = useState(false);
  const [showCptCode, setShowCptCode] = useState(false);
  const [showCptCode2, setShowCptCode2] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);

  const [measureCodeDict, setMeasureCodeDict] = useState<any>({});
  const [measureNoteDict, setMeasureNoteDict] = useState<any>({});

  const [initIcdCode, setInitIcdCode] = useState('');
  const [initIcdCode2, setInitIcdCode2] = useState('');
  const [initCptCode, setInitCptCode] = useState('');
  const [initCptCode2, setInitCptCode2] = useState('');

  const [usePercentile, setUsePercentile] = useState(false);
  const [bmiPercentile, setBmiPercentile] = useState(0);
  const [showBP, setShowBP] = useState(false);

  const [isDosModalVisble, setIsDosModalVisble] = useState(false);
  const [isEmCodeModalVisble, setIsEmCodeModalVisble] = useState(false);

  const [selectedDos, setSelectedDos] = useState('');
  const [primaryCode, setPrimaryCode] = useState<any>(null);
  const [measuresInDOS, setMeasuresInDOS] = useState<any>([]);

  const [emCodeSearchResult, setEmCodeSearchResult] = useState<any>([]);
  const [emCodes, setEmCodes] = useState<any>([]);
  const [modifierSearchResults, setModifierSearchResults] = useState<any>([]);
  const [modifierSearchInput, setModifierSearchInput] = useState<any>('');
  const [openMediCalRxProgramModal, setOpenMediCalRxProgramModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>([]);
  const [disableButton, setDisableButton] = useState(false);
  const [incompletedActiveKey, setIncompletedActiveKey] = useState<any>([]);
  const [inprogressActiveKey, setInprogressActiveKey] = useState<any>([]);
  const [completedActiveKey, setCompletedActiveKey] = useState<any>([]);
  const [vitalRequired, setVitalRequired] = useState(false);
  const [isDisableComponent, setIsDisableComponent] = useState(false);
  const [isDisableForMeasureYear, setIsDisableForMeasureYear] = useState(false);
  const [isClosingGapAlertVisible, setIsClosingGapAlertVisible] = useState(false);

  //used for update member measure
  const [measureFormDetails, setMeasureFormDetails] = useState({
    MeasureName: '',
    MeasureCode: '',
    CptCode: '',
    IcdCode: '',
    DateOfService: '',
    Screening: '',
    CptCode2: '',
    IcdCode2: '',
    Screening2: '',
    RmiFrequencyDuration: null,
    RmiFrequency: null,
  });

  useEffect(() => {
    setVitalRequired(props.eligibilityMemberInfo.RmiHdHighBPFlag === 'Y');
    getMemberImmunization();
    getEncounter();
    setClosingGapAlert();
  }, []);

  useEffect(() => {
    let lastDate = constants.LAST_DATE_FOR_MEASURE_UPDATE + "-" + (props.measureYear + 1);
    if (moment() > moment(lastDate)) {
      setIsDisableForMeasureYear(true);
    } else {
      setIsDisableForMeasureYear(false);
    }
    getMemberMeasures();
  }, [props.measureYear])

  const isUploadDocumentModalVisibleHandler = (command: string, documentIds: number[][]) => {
    if (command === 'submit') {
      // each measure uploads 1 attachment so there should be 1 attachment coming back from upload API response
      // example response for single category with 1 attachment upload = [123123]
      saveMemberMeasure(documentIds[0][0]);
    }
    setIsUploadDocumentModalVisible(false);
  }

  const memberImmunizationColumns = [
    {
      title: 'IMMUNIZATION',
      key: 'RmiVacCode',
      dataIndex: 'RmiVacCode',
    },
    {
      title: 'DATE',
      key: 'RmiVacDate',
      dataIndex: 'RmiVacDate',
      render: (_: string, record: any) => formatDate(record.RmiVacDate, 'MM/DD/YYYY')
    },
    {
      title: '	CVX',
      key: 'RmiCvxCode',
      dataIndex: 'RmiCvxCode',
    },
  ]

  const getMemberMeasures = () => {
    //close all panels
    setIncompletedActiveKey([]);
    setInprogressActiveKey([]);
    setCompletedActiveKey([]);

    return props.getMemberMeasures(props.eligibilityMemberInfo, props.measureYear, '')
      .then((response: any) => {
        if (response.length === 0) setDisableButton(true);

        isPediatrics();

        setInCompletedMeasures(_.orderBy(response, 'RmiMeasureName', 'asc').filter((measure: any) => measure.RmiMeasureStatus?.toLowerCase()  === 'incomplete'));

        setInProgressMeasures(
          _.map(
            _.groupBy(
              _.orderBy(response, 'RmiMeasureName', 'asc')
                .filter((measure: any) => measure.RmiFrequencyLimit > 1 &&
                  measure.RmiCompletedVisits < measure.RmiFrequencyLimit &&
                  measure.RmiDosFrom !== null ||
                  measure.RmiClaimStatus?.toLowerCase()  === 'processing' ||
                  (measure.RmiCompletedVisits < measure.RmiFrequencyLimit && measure.RmiMeasureStatus?.toLowerCase()  === 'readytosubmit')
                ), 'RmiMeasureCode'
            ),
            measure => _.maxBy(measure, 'RmiDosFrom')
          )
        );

        setCompletedMeasures(_.orderBy(response, 'RmiMeasureName', 'asc')
          .filter((measure: any) =>
            (measure.RmiFrequencyLimit === 1) &&
            (measure.RmiCompletedVisits === measure.RmiFrequencyLimit && measure.RmiClaimStatus?.toLowerCase()  === 'completed'))
        );

        /**
         * Control Blood Pressure and Care For Older Adults - Medication Review measures have IcdCode2 as a 2nd screening attached to the measure
         * that needs to be broken down to 2 measures to display for ready to submit popup
        */
        const readyToSubmitMeasures: any[] = [];
        for (const measure of response.filter((measure: any) => measure.RmiMeasureStatus?.toLowerCase()  === 'readytosubmit')) {
          if (measure.RmiIcdCode2) {
            readyToSubmitMeasures.push({
              ...measure,
              "RmiIcdCode": measure.RmiIcdCode2,
              "RmiCptCode": measure.RmiCptCode2
            });
          }
          readyToSubmitMeasures.push(measure);
        }

        setReadyToSubmitMeasures(_.chain(readyToSubmitMeasures).groupBy(item => moment(item.RmiDosFrom).format('MM/DD/YYYY')).value());
      })
      .catch((err: ErrorResponse) => {
        console.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const getMemberImmunization = () => {
    return props.getMemberImmunization(props.eligibilityMemberInfo.RmiMembMpiNo)
      .then((response: any) => {
        setMemberImmunization(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const setClaimDos = (date: string) => {
    setMeasureFormDetails((prev) => ({ ...prev, DateOfService: date }));
  }

  const getEncounter = () => {
    return props.getEncounter(props.eligibilityMemberInfo.RmiCompanyId, props.eligibilityMemberInfo.RmiMembMpiNo, dos)
      .then((response: any) => {
        //heightFeetVitals and heightInchesVitals are use to calculate the inch/feet conversion later
        setMemberVitalDetail({
          ...response,
          heightFeetVitals: response.RmiHeightInchesVitals ? convertHeight(response.RmiHeightInchesVitals).heightFeet : 0,
          heightInchesVitals: response.RmiHeightInchesVitals ? convertHeight(response.RmiHeightInchesVitals).heightInches : 0
        });

        //initialize all the vital section form fields
        form.setFieldsValue({
          RmiSelfReported: response.RmiSelfReported ? response.RmiSelfReported : 1,
          RmiSystolicVitals: response.RmiSystolicVitals ? response.RmiSystolicVitals : 0,
          RmiDiastolicVitals: response.RmiDiastolicVitals? response.RmiDiastolicVitals : 0,
          RmiWeightLbsVitals: response.RmiWeightLbsVitals? response.RmiWeightLbsVitals : 0,
          RmiBMIVitals: response.RmiBMIVitals ? response.RmiBMIVitals : 0,
          heightFeetVitals: response.RmiHeightInchesVitals ? convertHeight(response.RmiHeightInchesVitals).heightFeet : 0,
          heightInchesVitals: response.RmiHeightInchesVitals ? convertHeight(response.RmiHeightInchesVitals).heightInches : 0,
        });
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const setClosingGapAlert = () => {
    let endDate = constants.LAST_DATE_FOR_CLOSING_GAPS + "-" + moment().year();
    let startDate = '01-15-' + moment().year();

    if (moment() >= moment(startDate) && moment() <= moment(endDate)) {
      setIsClosingGapAlertVisible(true);
    } else {
      setIsClosingGapAlertVisible(false);
    }
  }

  const systolicVitalsRules = async () => {

    if (form.getFieldValue('RmiSystolicVitals') === '' || form.getFieldValue('RmiSystolicVitals') < 0) {
      return Promise.reject('Systolic vitals must be a positive number');
    }

    if (vitalRequired && ageInMonths >= 216 && ageInMonths <= 1032) {

      if (form.getFieldValue('RmiSystolicVitals') === 0 || form.getFieldValue('RmiSystolicVitals') === null || form.getFieldValue('RmiSystolicVitals') === '') {
        return Promise.reject(`Please enter the member's systolic/diastolic pressure`);
      }

      if (form.getFieldValue('RmiDiastolicVitals') === 0 || form.getFieldValue('RmiDiastolicVitals') === null || form.getFieldValue('RmiDiastolicVitals') === '') {
        return Promise.reject(`Please enter the member's systolic/diastolic pressure`);
      }

      if (form.getFieldValue('RmiSystolicVitals') && (form.getFieldValue('RmiSystolicVitals') < 70 || form.getFieldValue('RmiSystolicVitals') > 200)) {
        return Promise.reject('Systolic value must be between 70 - 200');
      }
    }

    return Promise.resolve();
  }

  const diastolicVitalsRules = async () => {

    if (form.getFieldValue('RmiDiastolicVitals') === '' || form.getFieldValue('RmiDiastolicVitals') < 0) {
      return Promise.reject('Diastolic vitals must be a positive number');
    }

    if (vitalRequired && ageInMonths >= 216 && ageInMonths <= 1032) {

      if (form.getFieldValue('RmiSystolicVitals') === 0 || form.getFieldValue('RmiSystolicVitals') === null || form.getFieldValue('RmiSystolicVitals') === '') {
        return Promise.reject(`Please enter the member's systolic/diastolic pressure`);
      }

      if (form.getFieldValue('RmiDiastolicVitals') === 0 || form.getFieldValue('RmiDiastolicVitals') === null || form.getFieldValue('RmiDiastolicVitals') === '') {
        return Promise.reject(`Please enter the member's systolic/diastolic pressure`);
      }

      else if (form.getFieldValue('RmiSystolicVitals') && (form.getFieldValue('RmiDiastolicVitals') < 30 || form.getFieldValue('RmiDiastolicVitals') > 120)) {
        return Promise.reject('Diastolic value must be between 30 - 120');
      }
    }

    return Promise.resolve();
  }

  const weightRules = async () => {

    if (form.getFieldValue('RmiWeightLbsVitals') === '' || form.getFieldValue('RmiWeightLbsVitals') < 0) {
      return Promise.reject(`Weight must be a positive number`);
    }

    if (vitalRequired && ageInMonths >= 36) {
      if (form.getFieldValue('RmiWeightLbsVitals') === null || form.getFieldValue('RmiWeightLbsVitals') === 0 || form.getFieldValue('RmiWeightLbsVitals') === '') {
        return Promise.reject(`Please enter the member's weight to calculate their BMI`);
      }
    }

    return Promise.resolve();
  }

  const feetRules = async () => {

    if (form.getFieldValue('heightFeetVitals') === '' || form.getFieldValue('heightFeetVitals') < 0) {
      return Promise.reject('Feet must be a positive number');
    }

    if (vitalRequired && ageInMonths >= 36) {
      if ((form.getFieldValue('heightFeetVitals') === 0 || form.getFieldValue('heightFeetVitals') === null || form.getFieldValue('heightFeetVitals') === '') &&
          (form.getFieldValue('heightInchesVitals') === 0 || form.getFieldValue('heightInchesVitals') === null || form.getFieldValue('heightInchesVitals') === '')) {
        return Promise.reject(`Please enter the member's height to calculate their BMI`);
      }
    }

    return Promise.resolve();
  }

  const inchesRules = async () => {

    if (form.getFieldValue('heightInchesVitals') === '' || form.getFieldValue('heightInchesVitals') < 0) {
      return Promise.reject('Inches must be a positive number');
    }

    if (form.getFieldValue('heightInchesVitals') > 12) {
      return Promise.reject('Inches must be less than 12');
    }

    if (vitalRequired && ageInMonths >= 36) {
      if ((form.getFieldValue('heightFeetVitals') === 0 || form.getFieldValue('heightFeetVitals') === null || form.getFieldValue('heightFeetVitals') === '') &&
          (form.getFieldValue('heightInchesVitals') === 0 || form.getFieldValue('heightInchesVitals') === null || form.getFieldValue('heightInchesVitals') === '')) {
        return Promise.reject(`Please enter the member's height to calculate their BMI`);
      }
    }

    return Promise.resolve();
  }

  const saveMemberVitals = () => {
    form.validateFields().then(() => {

      const rawBmi = computeBMI(memberVitalDetail.RmiWeightLbsVitals, memberVitalDetail.RmiHeightInchesVitals, 1);

      let finalMemberVitalDetail: any = {...memberVitalDetail};
      finalMemberVitalDetail.RmiBMIVitals = rawBmi.toFixed(1);

      return props.saveEncounter(finalMemberVitalDetail, props.eligibilityMemberInfo, dos)
        .then(() => {
          getEncounter();
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });
    });
  }

  /**
   * gets the Hedis measure screening, icdCode, cptCode, etc.
   */
  const getHedisMeasures = (measureCode: string, measureName: string) => {
    return props.getHedisMeasures(measureCode, measureName)
      .then((response: any[]) => {
        let measureNoteDict: any = {};
        let measureCodeDict: any = {};
        for (const measure of response) {
          const screening = measure.RmiScreening;
          const icdCode = measure.RmiIcdCode;
          const cptCode = measure.RmiCptCode;
          const measureDetailId = measure.RmiUid;

          //logic to add note to the dict for reference later
          if (!(measureCode in measureNoteDict)) {
            measureNoteDict[measureCode] = measure.RmiNote;
            setMeasureNoteDict(measureNoteDict);
          };

          //If the measure has a screening but not cpt and icd, this means this screening requires a file
          if (!cptCode && !icdCode) {
            if (!(measureCode in measureCodeDict)) {
              measureCodeDict[measureCode] = {};
            }
            if (measure.RmiUploadFlag === "Y") {
              measureCodeDict[measureCode][screening] = measureDetailId;
            }
          }
          //Otherwise, this screening requires icd and cpt filled out
          else {
            if (!(measureCode in measureCodeDict)) {
              measureCodeDict[measureCode] = {};
            }
            if (!(screening in measureCodeDict[measureCode])) {
              measureCodeDict[measureCode][screening] = {};
            }
            if (!(icdCode in measureCodeDict[measureCode][screening])) {
              measureCodeDict[measureCode][screening][icdCode] = {};
            }
            if (cptCode !== null) {
              measureCodeDict[measureCode][screening][icdCode][cptCode] = measureDetailId;
            }
            else { //Some ICD codes don't have CPT codes, set the ICD = to measureDetailId
              measureCodeDict[measureCode][screening][icdCode] = measureDetailId;
            }
          }
        }

        if (["MHCBP", "MHBPD", "BPD"].includes(measureCode)) {
          setHasControllingBloodPressureMeasure(true);
        }

        if (measureCode === "MHCOAMR") {
          setHasCareForAdultsMedReviewMeasure(true);
        }

        setMeasureCodeDict(measureCodeDict[measureCode]);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  /**
   * This is part of the vital section
   */
  const isPediatrics = () => {
    setUsePercentile(false);
    if (ageInMonths > 35) {
      setShowBP(true);
    }
    if (ageInMonths > 35 && ageInMonths < 229) {
      setBmiPercentile(computeBmiPercentile(ageInMonths / 12, memberVitalDetail.RmiBMIVitals, props.eligibilityMemberInfo.RmiSex));
      setUsePercentile(true);
    }

    return usePercentile;
  }

  /**
   * These are coming from the panel keys that are passed to the Collapse when opening
   * Reset measureFormDetails, showIcdCode, showCptCode every time we open accordion
   */
  const expandPanel = (measureKey: any, measureStatus: string) => {
    //closes all other panels outside of the selected measure
    if (measureStatus?.toLowerCase()  === 'incompleted') {
      setIncompletedActiveKey(measureKey);
      setInprogressActiveKey([]);
      setCompletedActiveKey([]);
    }
    if (measureStatus?.toLowerCase()  === 'inprogress') {
      setIncompletedActiveKey([]);
      setInprogressActiveKey(measureKey);
      setCompletedActiveKey([]);
    }
    if (measureStatus?.toLowerCase()  === 'completed') {
      setIncompletedActiveKey([]);
      setInprogressActiveKey([]);
      setCompletedActiveKey(measureKey);
    }

    setShowIcdCode(false);
    setShowCptCode(false);
    setShowIcdCode2(false);
    setShowCptCode2(false);
    setShowUploadFile(false);
    // TODO - should not need to reset this
    setHasControllingBloodPressureMeasure(false);
    setHasCareForAdultsMedReviewMeasure(false);

    if (measureKey) {
      const measureField = measureKey.split('+');

      const measureCode = measureField[0];
      const measureName = measureField[1];
      const screening = measureField[2];
      const screening2 = measureField[3];
      const icdCode = measureField[4];
      const icdCode2 = measureField[5];
      const cptCode = measureField[6];
      const cptCode2 = measureField[7];
      const dosFrom = measureField[8];
      const frequencyDuration = measureField[9]; // (ex. 1, 2, 3, etc. used with frequency)
      const frequency = measureField[10]; // (ex. "month", "yearly")
      const frequencyLimit = measureField[11];
      const completedVisits = measureField[12];

      //used to populate inprogress and completed measure selected fields
      setInitIcdCode(icdCode);
      setInitIcdCode2(icdCode2);
      setInitCptCode(cptCode);
      setInitCptCode2(cptCode2);

      //Disable editing for completed measures with upload document selected or measures with completed visits
      if (icdCode === 'null' && cptCode === 'null' || frequencyLimit === completedVisits) setIsDisableComponent(true);

      setMeasureFormDetails({
        MeasureName: measureName,
        MeasureCode: measureCode,
        CptCode: cptCode === "null" ? '' : cptCode,
        IcdCode: icdCode === "null" ? '' : icdCode,
        DateOfService: dosFrom === "null" ? '' : moment(dosFrom).format('MM/DD/YYYY'),
        Screening: screening === "null" ? '' : screening, //used to pre-select the screening under inprogress/completed measures
        CptCode2: cptCode2 === "null" ? '' : cptCode2,
        IcdCode2: icdCode2 === "null" ? '' : icdCode2,
        Screening2: screening2 === "null" ? '' : screening2,
        RmiFrequencyDuration: frequencyDuration === "null" ? null : frequencyDuration,
        RmiFrequency: frequency === "null" ? null : frequency,
      });
      getHedisMeasures(measureCode, measureName);
    }
  }

  // const setScreening = (screening: string, property: string) => {
  const setScreeningHandler = (screening: string, property: string) => {
    //number type because this upload file screening is assigned with a Uid instead of object
    if (typeof (measureCodeDict[screening]) === 'number') {
      setMeasureFormDetails((prev) => ({ ...prev, Screening: screening, CptCode: '', IcdCode: '' }));
      setShowIcdCode(false);
      setShowCptCode(false);
      setShowUploadFile(true);
    } else {
      setShowUploadFile(false);
    }

    if (property === 'Screening') {
      setMeasureFormDetails((prev) => ({ ...prev, Screening: screening, CptCode: '', IcdCode: '' }));
      if (typeof (measureCodeDict[screening]) === 'object') {
        setShowIcdCode(true);
        setShowCptCode(false);
      }
      else {
        setShowIcdCode(false);
        setShowCptCode(false);
      }
    }

    if (property === 'Screening2') {
      setMeasureFormDetails((prev) => ({ ...prev, Screening2: screening, CptCode2: '', IcdCode2: '', DateOfService2: '' }));
      if (typeof (measureCodeDict[screening]) === 'object') {
        setShowIcdCode2(true);
        setShowCptCode2(false);
      }
      else {
        setShowIcdCode2(false);
        setShowCptCode2(false);
      }
    }
  }

  const setIcdCodeHandler = (icdCode: string, property: string) => {
    if (property === 'IcdCode') {
      setMeasureFormDetails((prev) => ({ ...prev, IcdCode: icdCode, CptCode: '' }));
      //this measure does not have cpt code, then do not show cpt code
      if (typeof measureCodeDict[measureFormDetails.Screening][icdCode] === 'number') {
        setShowCptCode(false);
      } else {
        setShowCptCode(true);
      }
    }

    if (property === 'IcdCode2') {
      setMeasureFormDetails((prev) => ({ ...prev, IcdCode2: icdCode, CptCode2: '' }));
      //this measure does not have cpt code, then do not show cpt code
      if (typeof measureCodeDict[measureFormDetails.Screening2][icdCode] === 'number') {
        setShowCptCode2(false);
      } else {
        setShowCptCode2(true);
      }
    }
  }

  const setCptCodeHandler = (cptCode: string, property: string) => {
    if (property === 'CptCode') {
      setMeasureFormDetails((prev) => ({ ...prev, CptCode: cptCode }));
    }

    if (property === 'CptCode2') {
      setMeasureFormDetails((prev) => ({ ...prev, CptCode2: cptCode }));
    }
  }

  const setDatePickerHandler = (newDate: string) => {
    setMeasureFormDetails((prev) => ({ ...prev, DateOfService: newDate }));
  }

  const saveMemberMeasure = (documentId?: number, claimReferenceNo?: string) => {
    const measureCode = measureFormDetails.MeasureCode;
    const screening = measureFormDetails.Screening;
    const icdCode = measureFormDetails.IcdCode;
    const cptCode = measureFormDetails.CptCode;
    const screening2 = measureFormDetails.Screening2;
    const icdCode2 = measureFormDetails.IcdCode2;
    const cptCode2 = measureFormDetails.CptCode2;
    let measureDetailId;
    let measureDetailId2;
    let measureStatus = '';
    let claimStatus = '';

    //Checks to see if the screening has ICD or CPT code, if not then set the measure detail ID and var measure status to Complete since files turn into complete
    if (icdCode === "" && cptCode === "") {
      measureDetailId = measureCodeDict[screening];
      if (screening2 !== "")
        measureDetailId2 = measureCodeDict[screening2];
      measureStatus = "completed";
      claimStatus = "processing";
    }

    //there are a view measures that have icd code but no cpt code and that is allowed
    if (icdCode !== "" && cptCode === "") {
      measureDetailId = measureCodeDict[screening][icdCode];
      if (icdCode2 !== "" && cptCode2 === "")
        measureDetailId2 = measureCodeDict[screening2][icdCode2];
      measureStatus = "readytosubmit";
      claimStatus = "incomplete";
    }

    if (icdCode !== "" && cptCode !== "") {
      measureDetailId = measureCodeDict[screening][icdCode][cptCode];
      if (icdCode2 !== '' && cptCode2 !== '')
        measureDetailId2 = measureCodeDict[screening2][icdCode2][cptCode2];
      measureStatus = "readytosubmit";
      claimStatus = "incomplete";
    }

    props.saveMemberMeasure(measureCode, measureDetailId, measureDetailId2, measureFormDetails, props.eligibilityMemberInfo,
                            props.primaryCareProvider, claimStatus, measureStatus, props.measureYear, documentId, claimReferenceNo)
      .then((response: any) => {
        if (response === 'success') {
          notifier.success('Measure Updated successfully.');
          getMemberMeasures();
        } else {
          notifier.error('Measure did not update successfully.');
        }

      }).catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  /**
   * ================================================
   * Claim Generator Part 2 - DOS Popup
   * ================================================
   */
  const onCancel = () => {
    setEmCodes([]);
    setSelectedDos('');
    setErrorMessage([]);
    setPrimaryCode(null);
    setMeasuresInDOS(null);
    setIsDosModalVisble(false);
    setIsEmCodeModalVisble(false);
  }

  const onBack = () => {
    setIsDosModalVisble(true);
    setIsEmCodeModalVisble(false);
  }

  const onContinue = () => {
    setIsDosModalVisble(false);
    setIsEmCodeModalVisble(true);
  }

  const selectPrimaryCode = (primaryCode: Object, measuresInDOS: any[]) => {
    setPrimaryCode(primaryCode)
    setMeasuresInDOS(measuresInDOS);
  }

  const selectDos = (dos: string) => {
    setSelectedDos(dos);
    setPrimaryCode(null);
    setMeasuresInDOS(null);
  }

  const getEmCode = (search: string) => {
    if (search.length < 2) return;

    const payload = {
      "CPTCode": search,
      "RmiCompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "RmiHpCode": props.eligibilityMemberInfo.RmiHpCode,
      "RmiStartDate": primaryCode.RmiDosFrom,
      "RmiEndDate": primaryCode.RmiDosTo,
      "RmiSpecialtyProvKeyId":props.eligibilityMemberInfo.RmiProvKeyId,
      "RmiPhCode": "P",
      "RmiMembKeyId": props.eligibilityMemberInfo.RmiMembKeyId,
      "ICDCode": primaryCode.RmiIcdCode,
    };

    props.getEmCode(payload)
      .then((response: any) => {
        setEmCodeSearchResult(response);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });
  }

  const handleSelectEmCode = (emCode: string) => {
    const emCodeObject = JSON.parse(emCode || '{}');
    form.resetFields(['emCodeText']);
    if (emCodeObject.MedicalRxExclusion) {
      setOpenMediCalRxProgramModal(true);
    } else {
      //check for existing before adding
      const found = emCodes.filter((code: any) => code?.ServiceCode === emCodeObject.ServiceCode);
      if (found.length === 0) {
        setEmCodes((prev: any) => ([...prev, JSON.parse(emCode)]));
      }
    }
  }

  const addModifier = (serviceCode: any) => {
    let newArr: any[] = [...emCodes];
    const foundIndex = newArr.findIndex((emCode: any) => (emCode.ServiceCode === serviceCode));
    if (foundIndex > -1) {
      if (newArr[foundIndex].hasOwnProperty('modifiers')) {
        //checks for duplicates before adding
        const foundModifier = newArr[foundIndex].modifiers.filter((modifier: any) => modifier === modifierSearchInput);
        if (foundModifier.length === 0 && modifierSearchInput) {
          newArr[foundIndex].modifiers.push(modifierSearchInput);
        }
      }
      else {
        if (modifierSearchInput) {
          newArr[foundIndex].modifiers = [modifierSearchInput];
        }
      }
      setEmCodes(newArr);
      setModifierSearchInput('');
      form.resetFields(); //clear modifier search box afterwards
    }
  }

  const removeModifier = (e: React.MouseEvent<HTMLElement>, removeModifier: string, serviceCode: string) => {
    e.preventDefault(); //prevents the antd default close button

    let newArr: any[] = [...emCodes];
    const foundServiceCodeIndex = newArr.findIndex((emCode: any) => ( emCode.ServiceCode === serviceCode ));
    if (foundServiceCodeIndex > -1) {
      const foundModifierIndex = newArr[foundServiceCodeIndex].modifiers.findIndex((modifier: any) => (modifier === removeModifier));
      if (foundModifierIndex > -1) {
        newArr[foundServiceCodeIndex].modifiers.splice(foundModifierIndex, 1);
      }
    }
    setEmCodes(newArr);
  }

  const removeEMCode = (removeIndex: number) => {
    setEmCodes((prev: any) => (prev.filter((_: string, index: number) => index !== removeIndex)));
  }

  const handleSelectModifier = (modifier: any) => {
    //This is to fix the formatting of the autocomplete selected value instead of display a json
    const found = modifierSearchResults.find((search: any) => (search.RmiCodeDesc === modifier));
    setModifierSearchInput(found.RmiModif);
  }

  const submitMeasure = () => {
    let newArr = [...measuresInDOS];
    const memberInfo = props.eligibilityMemberInfo;
    const payload: any[] = [];

    //swap the selected primary code measure to the front of the array for it to be displayed as A under DIAG REF
    newArr.forEach(function (item, index) {
      if (item.RmiMeasureName === primaryCode.RmiMeasureName) {
        newArr.splice(index, 1);
        newArr.unshift(item);
      }
    });

    //from 1st popup
    for (const measure of newArr) {
      payload.push({
          "RmiMembMpiNo": memberInfo.RmiMembMpiNo,
          "RmiProvKeyId": memberInfo.RmiProvKeyId,
          "RmiDosFrom": measure.RmiDosFrom,
          "RmiDosTo": measure.RmiDosTo,
          "RmiUserId": sessionStorage.user_id,
          "RmiCompanyId": memberInfo.RmiCompanyId,
          "RmiIcdCode": measure.RmiIcdCode,
          "RmiCptCode": measure.RmiCptCode,
          "RmiCptModifier1": null,
          "RmiCptModifier2": null,
          "RmiCptModifier3": null,
          "RmiCptModifier4": null,
          "RmiIcdCodeDesc": null,
      });
    }

    //from 2nd popup
    for (const emCode of emCodes) {
      payload.push({
        "RmiMembMpiNo": memberInfo.RmiMembMpiNo,
        "RmiProvKeyId": memberInfo.RmiProvKeyId,
        "RmiDosFrom": primaryCode.RmiDosFrom,
        "RmiDosTo": primaryCode.RmiDosTo,
        "RmiUserId": sessionStorage.user_id,
        "RmiCompanyId": memberInfo.RmiCompanyId,
        "RmiIcdCode": primaryCode.RmiIcdCode,
        "RmiCptCode": emCode.RmiProcCode,
        "RmiCptModifier1": emCode.modifiers ? emCode.modifiers[0] : null,
        "RmiCptModifier2": emCode.modifiers ? emCode.modifiers[1] : null,
        "RmiCptModifier3": emCode.modifiers ? emCode.modifiers[2] : null,
        "RmiCptModifier4": emCode.modifiers ? emCode.modifiers[3] : null,
        "RmiIcdCodeDesc": "emCode"
      });
    }

    props.validateMeasures(payload)
      .then((response: any) => {
        if (response.length > 0) {
          setErrorMessage(response);
          return;
        }

        props.submitMemberMeasures(payload)
          .then((measures: any) => {
            history.push({
              pathname: '/eligibility/claim-final-submission',
              state: {
                eligibilityMemberInfo: props.eligibilityMemberInfo,
                primaryCareProvider: props.primaryCareProvider,
                dos: primaryCode.RmiDosFrom,
                measures: measures,
                measureYear: props.measureYear,
                breadcrumbs: [
                  { pathname: '/quality/overview', name: 'Home', icon: '' },
                  { pathname: '/eligibility/search', name: 'Member Search', icon: '' },
                  {
                    pathname: '/eligibility/member-detail', name: 'Member Eligibility and Assessment', icon: '',
                    state: { eligibilityMemberInfo: props.eligibilityMemberInfo }
                  },
                  { pathname: '/eligibility/claim-final-submission', name: 'Review Claim Detail', icon: '' }
                ]
              }
            });
          });
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const getAuthModifierCode = (search: string) => {
    if (search.length < 2) return;

    props.getAuthModifierCode()
      .then((response: any) => {
        setModifierSearchResults(response.filter((modifier: any) => ( modifier.RmiCodeDesc.includes(search.toUpperCase()) )).slice(0, 15));
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  return (
    <>
      <div className="claim-generator exclude-print">
        <div className="row">
          <div className="col-lg-24 col-md-24">
            <Tooltip placement="topLeft" title={isDisableForMeasureYear? ModalTitles.MEASURE_UPDATE_BLOCKED:""}>
              <Button
                className="green ml-4"
                size="large"
                disabled={disableButton || isDisableForMeasureYear}
                onClick={() => setIsDosModalVisble(true)}
                style={{ float: 'right' }}
                loading={props.isFetchingMember}
              >
                Create Claim
              </Button>
            </Tooltip>

            <Button
              className="ml-4"
              size="large"
              type="primary"
              style={{ float: 'right' }}
              onClick={() => getMemberMeasures()}
              loading={props.isFetchingMember}
            >
              Refresh Measures
            </Button>
          </div>
        </div>

        <Modal
          className="medicalrx-popup"
          visible={openMediCalRxProgramModal}
          footer={null}
        >
          <Title className="text-center" level={3}>Medi-Cal Rx Program</Title>
          <p style={{ textAlign: "center", fontSize: "14px" }}>
            Requested services are covered under Medi-Cal Rx Program. <br />
            Please choose one of the external links to submit your request: <br /> <br />
            <a href={MEDICAL_RX_URL} target="_blank"><b>{MEDICAL_RX_URL}</b></a>
            <br />
            <a href={COVER_MY_MED_URL} target="_blank"><b>{COVER_MY_MED_URL}</b></a>
          </p>
          <Button
            className="green"
            style={{left: "44%"}}
            key="ok"
            type="primary"
            onClick={() => setOpenMediCalRxProgramModal(false)}
            loading={false}
          >
            OK
          </Button>
        </Modal>

        <Modal
          className="dos-modal"
          visible={isDosModalVisble}
          width={1000}
          maskClosable={false}
          onCancel={onCancel} //used for the x button
          footer={[
            <Button
              className="green"
              key="continue"
              type="primary"
              onClick={onContinue}
              loading={false}
              disabled={!primaryCode}
            >
              Continue
            </Button>,
          ]}
        >
          <Title className="text-center" level={3}>Choose DOS To Submit Claim</Title>
          <Title className="text-center description-color" level={5}>Only measures completed from the same DOS can be submitted in the same claim. Please select a group of measures below to continue.</Title>

          <Divider/>

          {Object.keys(readyToSubmitMeasures).sort().map((dos: string) => (
            <div className="row flex-lg-wrap justify-content-start ml-0">
              <>
                <div className="col-lg-2 col-md-2 pl-4 pt-6">
                  <input
                    type="radio"
                    name="selectedDos"
                    key={dos}
                    value={moment(dos).format('MM/DD/YYYY')}
                    onChange={(e) => { selectDos(e.target.value) }}
                    checked={selectedDos === moment(dos).format('MM/DD/YYYY')}
                  />
                </div>
                <div className={"col-lg-21 col-md-21 mb-4 " + (selectedDos !== dos ? 'not-selected-table': 'selected-table')}>
                  <table style={{ textAlign: "left", width: '100%' }}>
                    <thead>
                      <tr>
                        <th align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{ padding: '8px', width: '60%' }}>DOS {formatDate(dos, 'MM/DD/YYYY')}</th>
                        <th style={{ width: '20%' }}></th>
                        <th style={{ width: '10%' }}></th>
                        <th style={{ width: '10%' }}></th>
                      </tr>
                      <tr>
                        <th align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{ width: '60%' }}>Measure</th>
                        <th align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{ width: '20%' }}>Select Primary Code</th>
                        <th align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{ width: '10%' }}>ICD</th>
                        <th align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{ width: '10%' }}>CPT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readyToSubmitMeasures[dos].map((measure: any) => (
                        <>
                          <tr>
                            <td align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{padding: '8px'}}>{measure.RmiMeasureName}</td>
                            <td align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{padding: '8px'}}>
                              <input
                                type="radio"
                                name="primaryCode"
                                key={dos}
                                onChange={() => selectPrimaryCode(measure, readyToSubmitMeasures[dos])}
                                checked={primaryCode === measure}
                                disabled={selectedDos !== moment(measure.RmiDosFrom).format('MM/DD/YYYY')}
                              />
                            </td>
                            <td align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{padding: '8px'}}>{measure.RmiIcdCode}</td>
                            <td align="center" className={selectedDos !== dos ? 'dos-black' : 'dos-white'} style={{padding: '8px'}}>{measure.RmiCptCode}</td>
                          </tr>
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            </div>
          ))}
        </Modal>

        <Modal
          className="emCode-modal"
          visible={isEmCodeModalVisble}
          width={1500}
          maskClosable={false}
          onCancel={onCancel}
          footer={[
            <Button
              style={{float: 'left', backgroundColor: 'black'}}
              key="back"
              type="primary"
              onClick={onBack}
              loading={false}
            >
              Back
            </Button>,
            <Button
              className="green"
              key="continue"
              type="primary"
              onClick={submitMeasure}
              loading={false}
            >
              Continue To Submission
            </Button>
          ]}
        >
          <Title className="text-center" level={3}>Choose Evaluation and Management Codes & Modifiers</Title>
          <Title className="text-center description-color" level={5}>Select the appropiate E/M codes and their modifiers before continuing to claim submission.</Title>
          {errorMessage.length > 0 && (<>
            <h4 className="error-color">
            <ul className="error-message mt-2 text-center">
              {errorMessage.map((message: string) =>
              (
                <>
                  <li>{message}</li>
                </>
              ))}
              </ul>
            </h4>
          </>)}
          <Divider/>

          <div className="row flex-lg-wrap justify-content-start mb-4 ml-1 mr-1">
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%"}}>
              <h4 className="description-black">1. Select E/M Codes</h4>
              <h5 className="description-color">{emCodes.length} Selected</h5>
            </div>
            <div className="col-lg-24 col-md-24">
            <Form
              form={form}
              name={"emCodeForm"}
              onFinish={handleSelectEmCode}
            >
              <FloatLabel
                label=""
                name="emCodeText"
                form={form}
                >
                  <AutoComplete
                    style={{ width: '100%' }}
                    onSearch={debounce(getEmCode, 500)}
                    onSelect={handleSelectEmCode}
                    placeholder="Search"
                    notFoundContent={'Not found. Please type more than 2 characters or try your search again.'}
                  >
                    {props.isFetchingEmCodes ? <Option value=""><Spin /></Option> : emCodeSearchResult.map((value: any) => (
                      <Option key={value.diagcode} value={JSON.stringify(value)}>
                        {value.ServiceCode}
                      </Option>
                    ))}
                  </AutoComplete>
              </FloatLabel>
            </Form>
            </div>
          </div>

          <div className="flex-lg-wrap justify-content-start mb-4 ml-1 mr-1">
            <div className="row">
              <div className="col-lg-24 col-md-24">
                <h4 className="description-black">2. Add Modifiers for Selected E/M Codes</h4>
              </div>
            </div>
            <div className="row">
              <div className="col-lg-24 col-md-24">
                <h5 className="text-left description-color">Modifier 25 is required if there are multiple E/M codes and must be appended to a sick visit.</h5>
              </div>
            </div>
          </div>
          <Form
            form={form}
            name={"modifierForm"}
            onFinish={addModifier}
          >
            {emCodes.map((emCode: any, index: number) => {
              return (
                <div className="row ml-1 mr-1" style={{ marginTop: "25px" }}>
                  <fieldset className="scheduler-border" key={index} style={{ width: "100%"}}>
                    <legend className="scheduler-border">{ emCode.ServiceCode } <Button className="remove-emcode" type="link" onClick={() => removeEMCode(index)}>Remove E/M Code</Button></legend>
                    <div className="control-group align-left">
                      <div className="row">
                        <div className="col-lg-12 col-md-12 pr-1">
                        <FloatLabel
                          label=""
                          name={"modifierText_" + index}
                          form={form}
                        >
                          <AutoComplete
                            style={{ width: '100%' }}
                            onSearch={debounce(getAuthModifierCode, 500)}
                            onSelect={handleSelectModifier}
                            placeholder="Search"
                            notFoundContent={'Not found. Please type more than 2 characters or try your search again.'}
                          >
                            {props.isFetchingEmCodes ? <Option value=""><Spin /></Option> : modifierSearchResults.map((value: any) => (
                              <Option key={value.diagcode} value={value.RmiCodeDesc}>
                                {value.RmiCodeDesc}
                              </Option>
                            ))}
                            </AutoComplete>
                          </FloatLabel>
                        </div>
                        <div className="col-lg-2 col-md-2 pl-1">
                          <Button
                            style={{marginTop: '9px'}}
                            className={"btn btn-sm " + (emCode?.modifiers?.length >= 4 ? 'dos-btn-disabled' : 'dos-btn-add')}
                            onClick={() => addModifier(emCode.ServiceCode)}
                            disabled={emCode?.modifiers?.length >= 4}
                          >
                            Add
                          </Button>
                        </div>
                        <div className="col-lg-10 col-md-10">
                          {emCode?.modifiers?.map((modifier: any, index: number) => {
                            return (
                              <>
                                <Tag
                                  closable
                                  className="control-group align-right"
                                  key={index} onClose={(e) => removeModifier(e, modifier, emCode.ServiceCode)}
                                >
                                  {modifier}
                                </Tag>
                              </>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              )
            })}
          </Form>
        </Modal>

        <UploadDocumentModal
          claimDos={measureFormDetails.DateOfService}
          setClaimDos={setClaimDos}
          eligibilityMemberInfo={props.eligibilityMemberInfo}
          isUploadDocumentModalVisibleHandler={isUploadDocumentModalVisibleHandler}
          isUploadDocumentModalVisible={isUploadDocumentModalVisible}
          uploadFilestoAuth={props.uploadFilestoAuth}
          getCategory={props.getCategory}
          isUploading={props.isUploading}
          uploadMultipleCategories={false}
          allowMultipleAttachments={false}
        />

        {props.isGetting ? (
          <>
            <Skeleton
              active
              paragraph={{ rows: 3 }}
            />
            <br />
            <Skeleton
              avatar
              active
              paragraph={{ rows: 3 }}
            />
            <br />
            <Skeleton
              active
              paragraph={{ rows: 3 }}
            />
          </>
        ) : (
          <>
            <Form
              form={form}
              name={'vitalForm'}
              onFinish={saveMemberVitals}
            >
              <div className="member-vital-section mb-4">
                <table className="table-bordered">
                  <tbody>
                    <tr>
                      <th><div className="ml-2 mt-2 mr-2">VITALS:</div></th>
                      <td>
                        <div className="self-reported-section ml-3 mt-3 mb-3">
                          <FloatLabel
                            label=""
                            name="RmiSelfReported"
                            form={form}
                            validateTrigger={['onSubmit']}
                          >
                            <Radio.Group onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, RmiSelfReported: e.target.value }))}>
                              <Radio key={1} value={1} checked={memberVitalDetail.RmiSelfReported === 1}>Patient Self-Reported</Radio><br />
                              <Radio key={0} value={0} checked={memberVitalDetail.RmiSelfReported === 0}>Not Captured Due To COVID-19</Radio><br />
                              <Radio key={2} value={2} checked={memberVitalDetail.RmiSelfReported === 2}>Captured in Office</Radio><br />
                            </Radio.Group>
                          </FloatLabel>
                        </div>
                      </td>

                      {showBP && (
                        <td style={{ verticalAlign: "top" }}>
                          <div className="bp-section ml-3">
                            <label className="inline-block">BP: Sys.</label>&nbsp;
                            <div className="systolicVitals">
                              <FloatLabel
                                label=""
                                name="RmiSystolicVitals"
                                form={form}
                                validateTrigger={['onSubmit']}
                                rules={[
                                  () => ({
                                    validator: systolicVitalsRules
                                  })
                                ]}
                              >
                                <Input
                                  type="number"
                                  className="short-input"
                                  onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, RmiSystolicVitals: e.target.value }))}
                                />
                              </FloatLabel>
                              &nbsp;mmHg&nbsp; &nbsp;/&nbsp;&nbsp; Dias.:&nbsp;&nbsp;
                            </div>
                            <div className="diastolicVitals">
                              <FloatLabel
                                className="RmiDiastolicVitals"
                                label=""
                                name="RmiDiastolicVitals"
                                form={form}
                                validateTrigger={['onSubmit']}
                                rules={[
                                  () => ({
                                    validator: diastolicVitalsRules
                                  })
                                ]}
                              >
                                <Input
                                  type="number"
                                  className="short-input"
                                  onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, RmiDiastolicVitals: e.target.value }))}
                                />
                              </FloatLabel>
                                &nbsp;mmHg
                            </div>
                          </div>
                        </td>
                      )}

                      <td style={{ verticalAlign: "top" }}>
                        <div className="measurement-section ml-3">
                          <label className="inline-block">Height:</label>&nbsp;
                          <div className="heightFeetVitals">
                            <FloatLabel
                              label=""
                              name="heightFeetVitals"
                              form={form}
                              validateTrigger={['onSubmit']}
                              rules={[
                                () => ({
                                  validator: feetRules
                                })
                              ]}
                            >
                              <Input
                                type="number"
                                className="short-input"
                                onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, heightFeetVitals: e.target.value }))}
                                onBlur={() => setMemberVitalDetail((prev: any) => ({ ...prev, RmiHeightInchesVitals: calculateHeightInInches(memberVitalDetail) }))}
                              />
                            </FloatLabel>
                            &nbsp;(ft.)&nbsp;&nbsp;
                          </div>

                          <div className="heightInchesVitals">
                            <FloatLabel
                              label=""
                              name="heightInchesVitals"
                              form={form}
                              validateTrigger={['onSubmit']}
                              rules={[
                                () => ({
                                  validator: inchesRules
                                })
                              ]}
                            >
                              <Input
                                type="number"
                                className="short-input"
                                onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, heightInchesVitals: e.target.value }))}
                                  onBlur={() => setMemberVitalDetail((prev: any) => ({...prev,RmiHeightInchesVitals: calculateHeightInInches(memberVitalDetail) }))}
                              />
                            </FloatLabel>
                            &nbsp;(in.)
                          </div>
                        </div>
                      </td>
                      <td style={{ verticalAlign: "top" }}>
                        <div className="weight-section ml-3">
                          Weight:&nbsp;
                          <FloatLabel
                            label=""
                            name="RmiWeightLbsVitals"
                            form={form}
                            validateTrigger={['onSubmit']}
                            rules={[
                              () => ({
                                validator: weightRules
                              })
                            ]}
                          >
                            <Input
                              type="number"
                              className="short-input"
                              onChange={(e) => setMemberVitalDetail((prev: any) => ({ ...prev, RmiWeightLbsVitals: e.target.value }))}
                            />
                          </FloatLabel>
                          &nbsp;(lbs.)
                          <button type="submit" className="ml-5" onClick={() => saveMemberVitals()}>
                            <i className="fa fa-save" ></i> Save
                          </button>
                        </div>
                      </td>
                      <td>
                        {ageInMonths > 228 && (
                            <span className="ml-3">
                            BMI: {(memberVitalDetail.RmiBMIVitals || 0).toFixed(1)}
                          </span>
                        )}

                        {ageInMonths <= 228 && (
                          <span>
                            Percentile:&nbsp; {bmiPercentile}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={5}>
                        <div className="row pt-3 pl-3 pb-3">
                          <div className="col-lg-6 col-md-5">
                            Last date of service:{" "}
                            {formatDate(memberVitalDetail.RmiDateOfService, 'MM/DD/YYYY')}
                          </div>
                          {showBP && (
                            <div className="col-lg-6 col-md-5">
                              BP: {memberVitalDetail.RmiSystolic} /{" "}
                              {memberVitalDetail.RmiDiastolic} mmHg
                            </div>
                          )}
                          <div className="col-lg-6 col-md-5">
                            Height: {convertHeight(memberVitalDetail.RmiHeightInches).heightFeet}&nbsp;feet
                            &nbsp;{convertHeight(memberVitalDetail.RmiHeightInches).heightInches}
                            &nbsp;inches
                          </div>
                          <div className="col-lg-3 col-md-3">
                            <span>Weight: {memberVitalDetail.RmiWeightLbs} lbs.</span>
                          </div>
                          <div className="col-lg-3 col-md-3">
                            BMI: {(memberVitalDetail.RmiBMIVitals || 0).toFixed(1)}
                          </div>
                          {ageInMonths <= 228 && (
                            <div className="col-lg-3 col-md-3">
                              Percentile: {bmiPercentile}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Form>
          </>
        )}

        {isClosingGapAlertVisible && (
          <>
            <div className="mb-3">
              <Alert
                message=""
                description={
                  <>
                    The last day to submit medical records to close HEDIS gaps is January 31st.
                    After this date, please submit a claim to close all gaps, if possible.
                  </>
                }
              />
            </div>
          </>
        )}

        {memberImmunization?.length > 0 && (
          <>
            <div className="immunization-section mb-4">
              <Collapse
                destroyInactivePanel={true}
              >
                <Panel
                  header="Immunization Record"
                  key="immunization-record"
                >
                  <Table
                    columns={memberImmunizationColumns}
                    dataSource={memberImmunization}
                    pagination={false}
                  />
                </Panel>
              </Collapse>
            </div>
          </>
        )}
        {props.isFetchingMember ? (
          <>
            <Skeleton
              active
              paragraph={{ rows: 3 }}
            />
            <br />
            <Skeleton
              avatar
              active
              paragraph={{ rows: 3 }}
            />
            <br />
            <Skeleton
              active
              paragraph={{ rows: 3 }}
            />
          </>
        ) : (
          <>
            <PageHeader
              className="incompleted-measures"
              title="Incomplete Measures"
            />

            <Form form={form} name="incompleted-measures">
              {inCompletedMeasures.map((measure: any, index: number) => (
                <Collapse
                  activeKey={incompletedActiveKey}
                  accordion={true}
                  onChange={(key) => expandPanel(key, 'incompleted')}
                  key={index}
                >
                  <Panel
                    header={measure.RmiMeasureName}
                    key={
                      measure.RmiMeasureCode +
                      "+" +
                      measure.RmiMeasureName +
                      "+" +
                      measure.RmiScreening +
                      "+" +
                      measure.RmiScreening2 +
                      "+" +
                      measure.RmiIcdCode +
                      "+" +
                      measure.RmiIcdCode2 +
                      "+" +
                      measure.RmiCptCode +
                      "+" +
                      measure.RmiCptCode2 +
                      "+" +
                      measure.RmiDosFrom +
                      "+" +
                      measure.RmiFrequencyDuration +
                      "+" +
                      measure.RmiFrequency +
                      "+" +
                      measure.RmiFrequencyLimit +
                      "+" +
                      measure.RmiCompletedVisits
                    }
                  >
                    {measureNoteDict[measure.RmiMeasureCode] && (
                      <>
                        <div className="row">
                          <div className="col-lg-24">
                            <Card className="helpful-text mb-5">
                              {measureNoteDict[measure.RmiMeasureCode]}
                            </Card>
                          </div>
                        </div>
                      </>
                    )}

                    {(BLOOD_PRESSURE_MEASURE_CODES.includes(measure.RmiMeasureCode)) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Compliant Office BP Reading - Upload Report:</label>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                      </>
                    )}

                    {(!hasControllingBloodPressureMeasure && !hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={''}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {showUploadFile && (
                              <div className="upload-file">
                                <>
                                  <label className="control-label label-measure">Upload a File:</label><br />
                                  <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                                </>
                              </div>
                            )}

                            <div className="row">
                              <div className="col-lg-12 col-md-12">
                                {(showIcdCode && !showUploadFile) && (
                                  <>
                                    <IcdCodeComponent
                                      measureCodeDict={measureCodeDict}
                                      measureFormDetails={measureFormDetails}
                                      property={'IcdCode'}
                                      setIcdCodeHandler={setIcdCodeHandler}
                                    />
                                  </>
                                )}
                              </div>

                              <div className="col-lg-12 col-md-12">
                                {showCptCode && (
                                  <>
                                    <CptCodeComponent
                                      measureCodeDict={measureCodeDict}
                                      measureFormDetails={measureFormDetails}
                                      property={'CptCode'}
                                      setCptCodeHandler={setCptCodeHandler}
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="col-lg-8 col-md-8">
                            <div className="row">
                              <div className="col-lg-24 col-md-24">
                                <DatepickerComponent
                                  measureFormDetails={measureFormDetails}
                                  form={form}
                                  setDatePickerHandler={setDatePickerHandler}
                                />
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-lg-24 col-md-24">
                                <div className="mt-4" style={{ float: 'right' }}>
                                  <Tooltip placement="topLeft" title={isDisableForMeasureYear? ModalTitles.MEASURE_UPDATE_BLOCKED:""}>
                                    <Button
                                      className="green"
                                      size="large"
                                      onClick={() => saveMemberMeasure()}
                                      disabled={
                                        measureFormDetails.Screening === '' ||
                                        measureFormDetails.IcdCode === '' ||
                                        (measureFormDetails.CptCode === '' && showCptCode) ||
                                        measureFormDetails.DateOfService === '' ||
                                        isDisableForMeasureYear
                                      }
                                    >
                                      Save
                                    </Button>
                                  </Tooltip>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {(hasControllingBloodPressureMeasure) && (
                      <>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={'Systolic'}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showIcdCode && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showCptCode && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                            />
                          </div>
                        </div>

                        <div className="row mt-5">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening2'}
                              screeningType={'Diastolic'}
                              setScreeningHandler={setScreeningHandler}
                            />

                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showIcdCode2 && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode2'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showCptCode2 && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode2'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            <div className="save-button">
                              <Tooltip placement="topLeft" title={isDisableForMeasureYear? ModalTitles.MEASURE_UPDATE_BLOCKED:""}>
                                <Button
                                  className="green"
                                  size="large"
                                  onClick={() => saveMemberMeasure()}
                                  disabled={
                                    measureFormDetails.Screening === ''                 ||
                                    measureFormDetails.IcdCode === ''                   ||
                                    (measureFormDetails.CptCode === '' && showCptCode)  ||
                                    measureFormDetails.DateOfService === ''             ||
                                    measureFormDetails.Screening2 === ''                ||
                                    measureFormDetails.IcdCode2 === ''                  ||
                                    measureFormDetails.CptCode2 === ''                  ||
                                    isDisableForMeasureYear
                                  }
                                >
                                  Save
                                </Button>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {(hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Medication list and/or review affirmation complete - Upload record</label>
                            </div>
                            <div className="col-lg-10 col-md-10">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-6 col-md-6">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening<span className="red"> (*)</span>:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening')} value={measureFormDetails.Screening}>
                              <div className="margin-radio-measure">
                                <Radio key={Object.keys(measureCodeDict)[0]} value={Object.keys(measureCodeDict)[0]}>{Object.keys(measureCodeDict)[0]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showIcdCode && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showCptCode && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-18">
                            <Divider>and</Divider>
                          </div>
                          <div className="col-lg-6">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening2')} value={measureFormDetails.Screening2}>
                              <div className="margin-radio-measure">
                              <Radio key={Object.keys(measureCodeDict)[1]} value={Object.keys(measureCodeDict)[1]}>{Object.keys(measureCodeDict)[1]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showIcdCode2 && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode2'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-4 col-md-4">
                            {showCptCode2 && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode2'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                            <br />
                          </div>

                          <div className="col-lg-8 col-md-8">
                            <div className="save-button">
                              <Tooltip placement="topLeft" title={isDisableForMeasureYear? ModalTitles.MEASURE_UPDATE_BLOCKED:""}>
                                <Button
                                  className="green"
                                  size="large"
                                  onClick={() => saveMemberMeasure()}
                                  disabled={
                                    measureFormDetails.Screening === ''                 ||
                                    measureFormDetails.IcdCode === ''                   ||
                                    (measureFormDetails.CptCode === '' && showCptCode)  ||
                                    measureFormDetails.DateOfService === ''             ||
                                    measureFormDetails.Screening2 === ''                ||
                                    measureFormDetails.IcdCode2 === ''                  ||
                                    measureFormDetails.CptCode2 === ''                  ||
                                    isDisableForMeasureYear
                                  }
                                >
                                  Save
                                </Button>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </Panel>
                </Collapse>
              ))}
            </Form>

            <PageHeader
              className="inprogress-measures"
              title="In-Progress Measures"
            />

            <Form form={form} name="inprogress-measures">
              {inProgressMeasures.map((measure: any, index: number) => (
                <Collapse
                  activeKey={inprogressActiveKey}
                  accordion={true}
                  onChange={(key) => expandPanel(key, 'inprogress')}
                  key={index}
                >
                  <Panel
                    header={measure.RmiMeasureName}
                    key={
                      measure.RmiMeasureCode +
                      "+" +
                      measure.RmiMeasureName +
                      "+" +
                      measure.RmiScreening +
                      "+" +
                      measure.RmiScreening2 +
                      "+" +
                      measure.RmiIcdCode +
                      "+" +
                      measure.RmiIcdCode2 +
                      "+" +
                      measure.RmiCptCode +
                      "+" +
                      measure.RmiCptCode2 +
                      "+" +
                      measure.RmiDosFrom +
                      "+" +
                      measure.RmiFrequencyDuration +
                      "+" +
                      measure.RmiFrequency +
                      "+" +
                      measure.RmiFrequencyLimit +
                      "+" +
                      measure.RmiCompletedVisits
                    }
                  >
                    {measureNoteDict[measure.RmiMeasureCode] && (
                      <>
                        <div className="row">
                          <div className="col-lg-24">
                            <Card className="helpful-text mb-5">
                              {measureNoteDict[measure.RmiMeasureCode]}
                            </Card>
                          </div>
                        </div>
                      </>
                    )}

                    {(BLOOD_PRESSURE_MEASURE_CODES.includes(measure.RmiMeasureCode)) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Compliant Office BP Reading - Upload Report:</label>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                      </>
                    )}

                    {(!hasControllingBloodPressureMeasure && !hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={''}
                              setScreeningHandler={setScreeningHandler}
                            />
                            <br />
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {showUploadFile && (
                              <div className="upload-file">
                                <>
                                  <label className="control-label label-measure">Upload a File:</label><br />
                                  <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                                </>
                              </div>
                            )}

                            { !showUploadFile ?
                            <div className="row">
                              <div className="col-lg-12 col-md-12">
                                {(!showIcdCode) || initIcdCode !== 'null' && (
                                  <>
                                    <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                                  </>
                                )}

                                {showIcdCode && (
                                  <>
                                    <IcdCodeComponent
                                      measureCodeDict={measureCodeDict}
                                      measureFormDetails={measureFormDetails}
                                      property={'IcdCode'}
                                      setIcdCodeHandler={setIcdCodeHandler}
                                    />
                                  </>
                                )}
                              </div>

                              <div className="col-lg-12 col-md-12">
                                {!showCptCode || initCptCode !== 'null' &&  (
                                  <>
                                    <label className="control-label label-measure">Selected CPT Code:</label><br /> { initCptCode } <br /><br />
                                  </>
                                )}

                                {showCptCode && (
                                  <>
                                    <CptCodeComponent
                                      measureCodeDict={measureCodeDict}
                                      measureFormDetails={measureFormDetails}
                                      property={'CptCode'}
                                      setCptCodeHandler={setCptCodeHandler}
                                    />
                                  </>
                                )}
                              </div>
                              </div>
                              : null
                            }
                          </div>

                          <div className="col-lg-8 col-md-8">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                            />
                          </div>
                        </div>

                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>

                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={false}
                              disabled={measure?.RmiClaimStatus?.toLowerCase()  === 'processing'}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Medication list and/or review affirmation complete - Upload record</label>
                            </div>
                            <div className="col-lg-10 col-md-10">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-6 col-md-6">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening')} value={measureFormDetails.Screening}>
                              <div className="margin-radio-measure">
                                <Radio key={Object.keys(measureCodeDict)[0]} value={Object.keys(measureCodeDict)[0]} checked={true}>{Object.keys(measureCodeDict)[0]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showIcdCode && initIcdCode !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                              </>
                            )}

                            {showIcdCode && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {!showCptCode || initCptCode !== 'null' && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode} <br /><br />
                              </>
                            )}

                            {showCptCode && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                            <br />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-18">
                            <Divider>and</Divider>
                          </div>
                          <div className="col-lg-6">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening2')} value={measureFormDetails.Screening2}>
                              <div className="margin-radio-measure">
                              <Radio key={Object.keys(measureCodeDict)[1]} value={Object.keys(measureCodeDict)[1]} checked={true}>{Object.keys(measureCodeDict)[1]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showIcdCode2 && initCptCode2 !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                              </>
                            )}

                            {showIcdCode2 && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode2'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showCptCode2 && initCptCode2 !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                              </>
                            )}

                            {showCptCode2 && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode2'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>
                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={true}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(hasControllingBloodPressureMeasure) && (
                      <>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={'Systolic'}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          {/* initIcdCode and initCptCode both null means it's an upload document measure, so don't show the selections */}
                          {(initIcdCode !== 'null' && initCptCode !== 'null') && (
                            <div className="col-lg-8 col-md-8">
                              <div className="row">
                                <div className="col-lg-12 col-md-12">
                                  {(!showIcdCode && initIcdCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                                    </>
                                  )}

                                  {showIcdCode && (
                                    <>
                                      <IcdCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'IcdCode'}
                                        setIcdCodeHandler={setIcdCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>

                                <div className="col-lg-12 col-md-12">
                                  {(!showCptCode && initIcdCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode} <br /><br />
                                    </>
                                  )}

                                  {showCptCode && (
                                    <>
                                      <CptCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'CptCode'}
                                        setCptCodeHandler={setCptCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="col-lg-8 col-md-8">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>

                        <div className="row mt-5">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening2'}
                              screeningType={'Diastolic'}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          {/* initIcdCode2 and initCptCode2 both null means it's an upload document measure, so don't show the selections */}
                          {(initIcdCode2 !== 'null' && initCptCode2 !== 'null') && (
                            <div className="col-lg-8 col-md-8">
                              <div className="row">
                                <div className="col-lg-12 col-md-12">
                                  {(!showIcdCode2 && initIcdCode2 !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode2} <br /> <br />
                                    </>
                                  )}

                                  {showIcdCode2 && (
                                    <>
                                      <IcdCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'IcdCode2'}
                                        setIcdCodeHandler={setIcdCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>

                                <div className="col-lg-12 col-md-12">
                                  {(!showCptCode2 && initCptCode2 !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                                    </>
                                  )}

                                  {showCptCode2 && (
                                    <>
                                      <CptCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'CptCode2'}
                                        setCptCodeHandler={setCptCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>
                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={true}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </Panel>
                </Collapse>
              ))}
            </Form>

            <PageHeader
              className="completed-measures"
              title="Completed Measures"
            />

            <Form form={form} name="completed-measures">
              {completedMeasures.map((measure: any, index: number) => (
                <Collapse
                  activeKey={completedActiveKey}
                  accordion={true}
                  onChange={(key) => expandPanel(key, 'completed')}
                  key={index}
                >
                  <Panel
                    header={measure.RmiMeasureName}
                    key={
                      measure.RmiMeasureCode +
                      "+" +
                      measure.RmiMeasureName +
                      "+" +
                      measure.RmiScreening +
                      "+" +
                      measure.RmiScreening2 +
                      "+" +
                      measure.RmiIcdCode +
                      "+" +
                      measure.RmiIcdCode2 +
                      "+" +
                      measure.RmiCptCode +
                      "+" +
                      measure.RmiCptCode2 +
                      "+" +
                      measure.RmiDosFrom +
                      "+" +
                      measure.RmiFrequencyDuration +
                      "+" +
                      measure.RmiFrequency +
                      "+" +
                      measure.RmiFrequencyLimit +
                      "+" +
                      measure.RmiCompletedVisits
                    }
                  >
                    {measureNoteDict[measure.RmiMeasureCode] && (
                      <div className="row">
                        <div className="col-lg-24">
                          <Card className="helpful-text mb-5">
                            {measureNoteDict[measure.RmiMeasureCode]}
                          </Card>
                        </div>
                      </div>
                    )}

                    {(BLOOD_PRESSURE_MEASURE_CODES.includes(measure.RmiMeasureCode)) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Compliant Office BP Reading - Upload Report:</label>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-8 col-md-8">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                      </>
                    )}

                    {(!hasControllingBloodPressureMeasure && !hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="row mb-4">
                          <div className={initIcdCode === 'null' && initCptCode === 'null' && !showUploadFile ? "col-lg-16 col-md-16" : "col-lg-6 col-md-6"}>
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={''}
                              setScreeningHandler={setScreeningHandler}
                              disabled={isDisableComponent}
                            />
                            <br />
                          </div>

                          {/* initIcdCode and initCptCode both null means it's an upload document measure, so don't show the selections */}
                          {(initIcdCode !== 'null' && initCptCode !== 'null') && (
                            <div className="col-lg-10 col-md-10">
                              {showUploadFile && (
                                <div className="upload-file">
                                  <>
                                    <label className="control-label label-measure">Upload a File:</label><br />
                                    <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                                  </>
                                </div>
                              )}

                              <div className="row">
                                <div className="col-lg-12 col-md-12">
                                  {(!showIcdCode && initIcdCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                                    </>
                                  )}

                                  {showIcdCode && (
                                    <>
                                      <IcdCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'IcdCode'}
                                        setIcdCodeHandler={setIcdCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>

                                <div className="col-lg-12 col-md-12">
                                  {(!showCptCode && initCptCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected CPT Code:</label><br /> { initCptCode } <br /><br />
                                    </>
                                  )}

                                  {showCptCode && (
                                    <>
                                      <CptCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'CptCode'}
                                        setCptCodeHandler={setCptCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="col-lg-8 col-md-8">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>

                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>

                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={false}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(hasControllingBloodPressureMeasure) && (
                      <>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening'}
                              screeningType={'Systolic'}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          {/* initIcdCode and initCptCode both null means it's an upload document measure, so don't show the selections */}
                          {(initIcdCode !== 'null' && initCptCode !== 'null') && (
                            <div className="col-lg-8 col-md-8">
                              <div className="row">
                                <div className="col-lg-12 col-md-12">
                                  {(!showIcdCode && initIcdCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                                    </>
                                  )}

                                  {showIcdCode && (
                                    <>
                                      <IcdCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'IcdCode'}
                                        setIcdCodeHandler={setIcdCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>

                                <div className="col-lg-12 col-md-12">
                                  {(!showCptCode && initIcdCode !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode} <br /><br />
                                    </>
                                  )}

                                  {showCptCode && (
                                    <>
                                      <CptCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'CptCode'}
                                        setCptCodeHandler={setCptCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="col-lg-8 col-md-8">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>

                        <div className="row mt-5">
                          <div className="col-lg-8 col-md-8">
                            <ScreeningComponent
                              measureCodeDict={measureCodeDict}
                              measureFormDetails={measureFormDetails}
                              property={'Screening2'}
                              screeningType={'Diastolic'}
                              setScreeningHandler={setScreeningHandler}
                            />
                          </div>

                          {/* initIcdCode2 and initCptCode2 both null means it's an upload document measure, so don't show the selections */}
                          {(initIcdCode2 !== 'null' && initCptCode2 !== 'null') && (
                            <div className="col-lg-8 col-md-8">
                              <div className="row">
                                <div className="col-lg-12 col-md-12">
                                  {(!showIcdCode2 && initIcdCode2 !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode2} <br /> <br />
                                    </>
                                  )}

                                  {showIcdCode2 && (
                                    <>
                                      <IcdCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'IcdCode2'}
                                        setIcdCodeHandler={setIcdCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>

                                <div className="col-lg-12 col-md-12">
                                  {(!showCptCode2 && initCptCode2 !== 'null') && (
                                    <>
                                      <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                                    </>
                                  )}

                                  {showCptCode2 && (
                                    <>
                                      <CptCodeComponent
                                        measureCodeDict={measureCodeDict}
                                        measureFormDetails={measureFormDetails}
                                        property={'CptCode2'}
                                        setCptCodeHandler={setCptCodeHandler}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>
                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={true}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(hasCareForAdultsMedReviewMeasure) && (
                      <>
                        <div className="upload-file">
                          <div className="row">
                            <div className="col-lg-8 col-md-8">
                              <label className="control-label label-measure">Medication list and/or review affirmation complete - Upload record</label>
                            </div>
                            <div className="col-lg-10 col-md-10">
                              <Button type="primary" className="mr-2" disabled={isDisableForMeasureYear} onClick={() => setIsUploadDocumentModalVisible(true)}>Upload Documents</Button>
                            </div>
                            <div className="col-lg-6 col-md-6">
                              <DatepickerComponent
                                measureFormDetails={measureFormDetails}
                                form={form}
                                setDatePickerHandler={setDatePickerHandler}
                              />
                            </div>
                          </div>
                        </div>
                        <Divider>or</Divider>
                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening')} value={measureFormDetails.Screening}>
                              <div className="margin-radio-measure">
                                <Radio key={Object.keys(measureCodeDict)[0]} value={Object.keys(measureCodeDict)[0]} checked={true}>{Object.keys(measureCodeDict)[0]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showIcdCode && initIcdCode !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected ICD Code:</label><br /> {initIcdCode} <br /> <br />
                              </>
                            )}

                            {showIcdCode && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {!showCptCode || initCptCode !== 'null' && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode} <br /><br />
                              </>
                            )}

                            {showCptCode && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-18">
                            <Divider>and</Divider>
                          </div>
                          <div className="col-lg-6">
                            <DatepickerComponent
                              measureFormDetails={measureFormDetails}
                              form={form}
                              setDatePickerHandler={setDatePickerHandler}
                            />
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-lg-8 col-md-8">
                            <label className="control-label label-measure">Select a Screening:</label><br />
                            <Radio.Group onChange={(e) => setScreeningHandler(e.target.value, 'Screening2')} value={measureFormDetails.Screening2}>
                              <div className="margin-radio-measure">
                              <Radio key={Object.keys(measureCodeDict)[1]} value={Object.keys(measureCodeDict)[1]} checked={true}>{Object.keys(measureCodeDict)[1]}</Radio>
                              </div>
                            </Radio.Group>
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showIcdCode2 && initCptCode2 !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                              </>
                            )}

                            {showIcdCode2 && (
                              <>
                                <IcdCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'IcdCode2'}
                                  setIcdCodeHandler={setIcdCodeHandler}
                                />
                              </>
                            )}
                          </div>

                          <div className="col-lg-8 col-md-8">
                            {(!showCptCode2 && initCptCode2 !== 'null') && (
                              <>
                                <label className="control-label label-measure">Selected CPT Code:</label><br /> {initCptCode2} <br /><br />
                              </>
                            )}

                            {showCptCode2 && (
                              <>
                                <CptCodeComponent
                                  measureCodeDict={measureCodeDict}
                                  measureFormDetails={measureFormDetails}
                                  property={'CptCode2'}
                                  setCptCodeHandler={setCptCodeHandler}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        <div className="row mt-4">
                          <div className="col-lg-8 col-md-8">
                            <MeasureStatusComponent
                              measure={measure}
                            />
                          </div>
                          <div className="col-lg-16 col-md-16">
                            <CompletionProgressComponent
                              measure={measure}
                              measureFormDetails={measureFormDetails}
                              saveMemberMeasure={saveMemberMeasure}
                              twoScreenings={true}
                              disabled={isDisableComponent}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </Panel>
                </Collapse>
              ))}
            </Form>
          </>
        )}
        <Tooltip placement="topLeft" title={isDisableForMeasureYear? ModalTitles.MEASURE_UPDATE_BLOCKED:""}>
          <Button
            className="green mt-3"
            size="large"
            onClick={() => setIsDosModalVisble(true)}
            style={{ float: 'right' }}
            disabled={disableButton || isDisableForMeasureYear}
            loading={props.isFetchingMember}
          >
            Create Claim
          </Button>
        </Tooltip>
      </div>
    </>
  );
}

export default ClaimGenerator;